import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import api from '../services/api';
import Swal from 'sweetalert2';
import { Icon } from '@iconify/react';
import PlanDetailCard from '../components/PlanDetailCard';

const ClinicSetupPage = () => {
    const { token } = useParams();
    const navigate = useNavigate();
    const [loading, setLoading] = useState(true);
    const [clinicData, setClinicData] = useState(null);
    const [plan, setPlan] = useState(null);
    const [processing, setProcessing] = useState(false);

    useEffect(() => {
        const fetchSetupData = async () => {
            try {
                // Public endpoint to get invitation details
                const response = await api.get(`/public/clinic-setup/${token}`);
                setClinicData(response.data.clinic);
                setPlan(response.data.plan);
                setLoading(false);
            } catch (error) {
                console.error('Setup Error:', error);

                if (error.response?.data?.redirect) {
                    Swal.fire({
                        icon: 'info',
                        title: 'Already Active',
                        text: error.response.data.message,
                        confirmButtonText: 'Go to Login'
                    }).then(() => navigate(error.response.data.redirect));
                } else {
                    Swal.fire({
                        icon: 'error',
                        title: 'Invalid Link',
                        text: 'This invitation link is invalid or has expired.',
                        confirmButtonText: 'Go to Login'
                    }).then(() => navigate('/login'));
                }
            }
        };

        if (token) fetchSetupData();
    }, [token, navigate]);

    const loadRazorpay = () => {
        return new Promise((resolve) => {
            const script = document.createElement('script');
            script.src = 'https://checkout.razorpay.com/v1/checkout.js';
            script.onload = () => resolve(true);
            script.onerror = () => resolve(false);
            document.body.appendChild(script);
        });
    };

    const handlePayment = async () => {
        setProcessing(true);
        const res = await loadRazorpay();

        if (!res) {
            Swal.fire('Error', 'Razorpay SDK failed to load. Are you online?', 'error');
            setProcessing(false);
            return;
        }

        try {
            // 1. Create Order on Backend
            const orderRes = await api.post('/public/create-payment-order', {
                clinicId: clinicData._id,
                planId: plan._id
            });

            const { id: orderId, currency, amount, keyId } = orderRes.data;

            // 2. Open Razorpay
            const options = {
                key: keyId,
                amount: amount,
                currency: currency,
                name: "Medoro Health",
                description: `Subscription for ${plan.name} Plan`,
                order_id: orderId,
                handler: async function (response) {
                    try {
                        // 3. Verify Payment
                        await api.post('/public/clinic-payment-success', {
                            razorpay_payment_id: response.razorpay_payment_id,
                            razorpay_order_id: response.razorpay_order_id,
                            razorpay_signature: response.razorpay_signature,
                            token: token // Security token
                        });

                        Swal.fire({
                            icon: 'success',
                            title: 'Setup Complete!',
                            text: 'Your clinic is now active. Please login to continue.',
                            timer: 3000,
                            showConfirmButton: false
                        }).then(() => navigate('/login'));

                    } catch (err) {
                        Swal.fire('Error', 'Payment verification failed. Contact support.', 'error');
                    }
                },
                prefill: {
                    name: clinicData.adminId.name,
                    email: clinicData.adminId.email,
                    contact: clinicData.adminId.phone
                },
                theme: {
                    color: "#0d6efd"
                },
                modal: {
                    ondismiss: function () {
                        Swal.fire('Cancelled', 'Transaction cancelled', 'error');
                        setProcessing(false);
                    }
                }
            };

            const paymentObject = new window.Razorpay(options);
            paymentObject.on('payment.failed', function (response) {
                Swal.fire('Error', response.error.description, 'error');
                setProcessing(false);
            });
            paymentObject.open();


        } catch (error) {
            console.error('Payment Error:', error);
            Swal.fire('Error', 'Failed to initiate payment', 'error');
            setProcessing(false);
        }
    };

    if (loading) {
        return (
            <div className="d-flex w-100 vh-100 justify-content-center align-items-center bg-base">
                <div className="text-center">
                    <div className="spinner-border text-primary" role="status"></div>
                    <p className="mt-3 text-secondary fw-medium">Loading Setup...</p>
                </div>
            </div>
        );
    }

    return (
        <div className="min-vh-100 bg-base d-flex flex-column align-items-center justify-content-center p-4">
            <div className="w-100" style={{ maxWidth: '900px' }}>
                <div className="text-center mb-48">
                    <h1 className="fw-bold text-primary-dark mb-2">Welcome to Medoro</h1>
                    <p className="text-secondary text-lg">Complete your setup for <strong>{clinicData?.name}</strong></p>
                </div>

                <div className="row g-4 justify-content-center">
                    <div className="col-lg-5">
                        <div className="card border-0 shadow-lg radius-16 overflow-hidden h-100">
                            <div className="card-header bg-primary-600 py-24 px-32 border-0">
                                <h5 className="text-white mb-0 fw-bold">Overview</h5>
                            </div>
                            <div className="card-body p-32">
                                <div className="mb-24">
                                    <h6 className="text-secondary-light text-sm text-uppercase fw-bold mb-2 ls-1">Clinic Details</h6>
                                    <p className="fw-semibold text-dark mb-1 text-lg">{clinicData?.name}</p>
                                    <p className="text-secondary mb-0"><Icon icon="solar:map-point-bold-duotone" className="me-1" /> {clinicData?.address}</p>
                                </div>
                                <hr className="border-neutral-200 my-24" />
                                <div className="mb-24">
                                    <h6 className="text-secondary-light text-sm text-uppercase fw-bold mb-2 ls-1">Administrator</h6>
                                    <div className="d-flex align-items-center gap-3">
                                        <div className="w-40-px h-40-px radius-circle bg-primary-50 text-primary-600 d-flex justify-content-center align-items-center text-lg fw-bold">
                                            {clinicData?.adminId?.name.charAt(0)}
                                        </div>
                                        <div>
                                            <p className="fw-semibold text-dark mb-0">{clinicData?.adminId?.name}</p>
                                            <p className="text-secondary text-sm mb-0">{clinicData?.adminId?.email}</p>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>

                    <div className="col-lg-6">
                        <div className="h-100 d-flex flex-column">
                            <div className="mb-24 flex-grow-1">
                                <h6 className="text-secondary mb-3 fw-medium">Selected Plan</h6>
                                <PlanDetailCard plan={plan} isSelected={true} />
                            </div>

                            <button
                                onClick={handlePayment}
                                disabled={processing}
                                className="btn btn-primary w-100 py-16 radius-12 fw-bold text-lg shadow-primary-lg d-flex align-items-center justify-content-center gap-2 hover-scale-sm transition-all"
                            >
                                {processing ? (
                                    <>Processing...</>
                                ) : (
                                    <>
                                        Pay {plan?.currency === 'INR' ? '₹' : '$'}{plan?.price} & Activate
                                        <Icon icon="solar:card-send-bold" />
                                    </>
                                )}
                            </button>
                            <p className="text-center text-xs text-secondary mt-3">
                                <Icon icon="solar:shield-check-bold" className="me-1 text-success-main" />
                                Secure payment via Razorpay
                            </p>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default ClinicSetupPage;
